import React, { useMemo } from "react";
import { EntityTablePage, DeleteConfirmationConfig } from "@/components/EntityTablePage";
import { getContactTableConfig } from "@/config/tableConfigs/contactTableConfig";
import { contactService } from "@/services/contactService";
import useAuth from "@/hooks/useAuth";
import type { ContactType } from "@/types/db/contacts";
import Head from "expo-router/head";
import { FromDB } from "@/types";
import { NoPermissionScreen } from "@/components/screens/shared/NoPermissionScreen";
import { DeleteContactModal } from "@/components/Modals/DeleteContactModal";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useSetPageTitle } from "@/hooks/useSetPageTitle";

export default function ContactsList() {
  const { userRoles, displayNames } = useAuth();
  const tableConfig = getContactTableConfig(userRoles, displayNames);
  useSetPageTitle(displayNames?.contacts?.plural);
  // Check if user has contacts read permission
  const hasContactsAccess = useMemo(() => {
    return userRoles?.some((r) => r.permissions?.contacts?.read === true) ?? false;
  }, [userRoles]);

  const deleteConfirmation: DeleteConfirmationConfig<FromDB<ContactType>> = useMemo(
    () => ({
      storageKey: "contact_delete_skip_confirmation",
      renderModal: ({ count, onConfirm, onCancel }) => (
        <DeleteContactModal
          count={count}
          onConfirm={async (dontShowAgain) => {
            if (dontShowAgain) {
              await AsyncStorage.setItem("contact_delete_skip_confirmation", "true");
            }
            onConfirm();
          }}
          onCancel={onCancel}
        />
      ),
    }),
    [],
  );

  if (!hasContactsAccess) {
    return <NoPermissionScreen entityName={displayNames?.contacts?.plural || "Contacts"} />;
  }

  return (
    <>
      <Head>
        <title>RockForce Contacts</title>
        <meta name="description" content="" />
      </Head>
      <EntityTablePage<FromDB<ContactType>>
        {...tableConfig}
        fetchData={contactService.getContacts}
        updateEntities={contactService.saveRowPatches}
        deleteEntities={contactService.deleteContactsByIds}
        deleteConfirmation={deleteConfirmation}
      />
    </>
  );
}
